import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { signInWithGoogle } from "../../utils/auth";
import { buildTenantPath, extractTenantSlugFromPathname } from "../../utils/tenantSlug";

interface LoginForm {
  email: string;
  password: string;
}

const LoginPage = () => {
  const { login, user, isAuthenticated, isClientSelected, selectedClient, isLoading: isAuthLoading, getDashboardPath } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  const tenantSlug = extractTenantSlugFromPathname(location.pathname);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>();

  // Redirect if user is authenticated and has selected a client
  if (isAuthenticated && isClientSelected) {
    const redirectPath = selectedClient?.userRole
      ? getDashboardPath(selectedClient.userRole, selectedClient.clientSlug || tenantSlug)
      : buildTenantPath("/", tenantSlug);
    return <Navigate to={redirectPath} replace />;
  }

  if (isAuthenticated && user?.userGlobalRole === "superAdmin") {
    return <Navigate to={buildTenantPath("/super-admin", tenantSlug)} replace />;
  }
  
  // Redirect to client selection if authenticated but no client is selected
  if (isAuthenticated && !isClientSelected) {
      return <Navigate to={buildTenantPath("/selecionar-cliente", tenantSlug)} replace />;
  }

  const getErrorMessage = (error: any) => {
    if (error.message.includes("permissão") || 
        error.message.includes("perfil atribuído") ||
        error.message.includes("contate o administrador")) {
      return error.message;
    }

    switch (error.code) {
      case "auth/invalid-credential":
        return "Email ou senha incorretos. Por favor, verifique suas credenciais.";
      case "auth/user-not-found":
        return "Email não cadastrado. Por favor, crie uma conta primeiro.";
      case "auth/wrong-password":
        return "Senha incorreta. Por favor, tente novamente.";
      case "auth/too-many-requests":
        return "Muitas tentativas de login. Por favor, tente novamente mais tarde.";
      case "auth/user-disabled":
        return "Esta conta foi desativada. Entre em contato com o suporte.";
      case "auth/network-request-failed":
        return "Erro de conexão. Verifique sua internet e tente novamente.";
      case "permission-denied":
        return "Você não tem permissão para acessar esta área. Por favor, contate o administrador.";
      default:
        return "Erro ao fazer login. Por favor, tente novamente ou contate o suporte.";
    }
  };

  const handleGoogleLogin = async () => {
    setIsSubmitting(true);
    try {
      await signInWithGoogle();
    } catch (error: any) {
      console.error("Google login error:", error);
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage, { autoClose: 5000 });
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (data: LoginForm) => {
    setIsSubmitting(true);
    try {
      await login(data.email, data.password);
      toast.success("Login bem-sucedido! Redirecionando...");
    } catch (error: any) {
      console.error("Login error:", error);
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage, { autoClose: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const isLoading = isAuthLoading || isSubmitting;

  return (
    <div className="min-h-screen bg-neutral-50 py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-md mx-auto">
          <Link to={buildTenantPath("/", tenantSlug)} className="inline-flex items-center text-sm text-neutral-600 hover:text-primary-600 mb-8">
            <ArrowLeft size={16} className="mr-1" />
            Voltar para o início
          </Link>

          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-display font-bold text-neutral-900 mb-2">
                Bem-vindo de volta
              </h1>
              <p className="text-neutral-600">
                Faça login para acessar sua área
              </p>
            </div>

            <div className="space-y-4 mb-8">
              <button
                onClick={handleGoogleLogin}
                disabled={isLoading}
                className="w-full flex items-center justify-center px-4 py-2 border border-neutral-300 rounded-md shadow-sm text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50"
              >
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5 mr-2" />
                Continuar com Google
              </button>
            </div>

            <div className="relative mb-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-neutral-500">Ou continue com email</span>
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail size={18} className="text-neutral-500" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    {...register("email", {
                      required: "Email é obrigatório",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Email inválido"
                      }
                    })}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-1">
                  Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock size={18} className="text-neutral-500" />
                  </div>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    {...register("password", { required: "Senha é obrigatória" })}
                  />
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <Link to={buildTenantPath("/recuperar-senha", tenantSlug)} className="font-medium text-primary-600 hover:text-primary-500">
                    Esqueceu sua senha?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isLoading ? (
                  <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                ) : (
                  "Entrar"
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-neutral-600">
                Ainda não tem uma conta?{" "}
                <Link to={buildTenantPath("/cadastro", tenantSlug)} className="font-medium text-primary-600 hover:text-primary-500">
                  Cadastre-se
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
